import moment from 'moment';


export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

export const hasNotch = () => {
	/**
	 * For storybook test
	 */
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (
		(iPhone && aspect.toFixed(3) === '0.462') ||
		(storybook && aspectFrame.toFixed(3) === '0.462')
	);
};

export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

export const priceFormat = (price: number) => {
	return price.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
};

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const percent = (value1: number, value2: number) =>
	Number(((value1 / value2 - 1) * 100).toFixed(2));

export const getFirstLetter = (text: string, letterCount = 2): string =>
	// @ts-ignore
	text
		.toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

export const formatDate = (date: string) => {
	return !date ? '-' : moment(date).format('DD MMM, YYYY');
};

export const formatCash = (cash: number, currency: string) => {
	return new Intl.NumberFormat('en-GB', {
		currency,
		style: 'currency',
	}).format(cash);
};

export const getStatusColor = (status: string): string => {
	switch (status?.toLowerCase()) {
		case 'approved':
			return 'success';
		case 'declined':
			return 'danger';
		case 'requested':
			return 'warning';
		case 'pending':
			return '#FFC107';
		case 'assigned':
			return '#2196F3';
		case 'picked_up':
			return '#9C27B0';
		case 'in_progress':
			return '#3F51B5';
		case 'in_transit':
			return '#3F51B5';
		case 'delivered':
			return '#4CAF50';
		case 'cancelled':
			return '#F44336';
		default:
			return 'secondary';
	}
};

export const getStatusIcon = (status: string) => {
	switch (status) {
		case 'pending':
			return 'HourglassEmpty';
		case 'assigned':
			return 'AssignmentInd';
		case 'picked_up':
			return 'LocalShipping';
		case 'in_progress':
			return 'DirectionsCar';
		case 'in_transit':
			return 'DirectionsCar';
		case 'delivered':
			return 'CheckCircle';
		case 'cancelled':
			return 'Cancel';
		default:
			return 'Info';
	}
};

export const formatMoney = (amount, currency = 'GMD', locale = 'en-GM') => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
	}).format(amount);
};

// Float type badge color helper
export const getFloatTypeBadge = (type: string) => {
	switch (type) {
		case 'float-in':
		case 'deposit':
			return { className: 'badge bg-success text-white', label: 'Float-In' };
		case 'float-out':
		case 'withdrawal':
			return { className: 'badge bg-danger text-white', label: 'Float-Out' };
		default:
			return { className: 'badge bg-secondary text-white', label: type };
	}
};

// Float status badge color helper
export const getFloatStatusBadge = (status: string) => {
	switch (status) {
		case 'pending':
			return { className: 'badge bg-warning text-dark', label: 'Pending' };
		case 'accepted':
			return { className: 'badge bg-green-500 text-white', label: 'Accepted' };
		case 'rejected':
			return { className: 'badge bg-danger text-white', label: 'Rejected' };
		case 'cancelled':
			return { className: 'badge bg-secondary text-white', label: 'Cancelled' };
		case 'active':
			return { className: 'badge bg-success text-white', label: 'Active' };
		default:
			return { className: 'badge bg-secondary text-white', label: status };
	}
};

// Permission organization interfaces
export interface Permission {
	permId: string;
	permName: string;
	description: string;
}

export interface PermissionCategory {
	permissions: Permission[];
	icon: string;
	children?: Record<string, PermissionCategory>;
}

// Permission organization helper - organizes permissions by menu hierarchy
export const organizePermissionsByMenu = (permissions: Permission[]): Record<string, PermissionCategory> => {
	if (!permissions.length) return {};

	const organized: Record<string, PermissionCategory> = {
		'Dashboard': { permissions: [], icon: 'Dashboard' },
		'Manage Transactions': { permissions: [], icon: 'ReceiptLong' },
		'Event Management': { permissions: [], icon: 'Event' },
		'Float Requests': { permissions: [], icon: 'AccountBalanceWallet' },
		'Refund Requests': { permissions: [], icon: 'RequestQuote' },
		'Delivery Services': { permissions: [], icon: 'ElectricBike', children: {
			'Delivery Transactions': { permissions: [], icon: 'LocalShipping' },
			'Manage Delivery Agents': { permissions: [], icon: 'DeliveryDining' },
		}},
		'Compliance Mngt': { permissions: [], icon: 'Https', children: {
			'Customer Compliance': { permissions: [], icon: 'VerifiedUser' },
			'Merchant Compliance': { permissions: [], icon: 'BusinessCenter' },
			'Business Compliance': { permissions: [], icon: 'Business' },
			'Agent Compliance': { permissions: [], icon: 'Badge' },
			'Delivery Compliance': { permissions: [], icon: 'Security' },
		}},
		'System Mngt': { permissions: [], icon: 'ManageAccounts', children: {
			'Delivery Rates': { permissions: [], icon: 'DeliveryDining' },
			'Exchange Rates': { permissions: [], icon: 'BackupTable' },
			'Service Fees': { permissions: [], icon: 'MiscellaneousServices' },
			'Ticket Settings': { permissions: [], icon: 'ConfirmationNumber' },
			'User Mngt': { permissions: [], icon: 'People' },
			'Roles & Permissions': { permissions: [], icon: 'Settings' },
			'Cashback Rules': { permissions: [], icon: 'AttachMoney' },
			'Banks': { permissions: [], icon: 'AccountBalance' },
			'Chart of Accounts': { permissions: [], icon: 'ReceiptLong' },
			'Adverts': { permissions: [], icon: 'Campaign' },
		}},
		'Network Mngt': { permissions: [], icon: 'Podcasts', children: {
			'Agent Mngt': { permissions: [], icon: 'Group' },
			'Merchant Mngt': { permissions: [], icon: 'Person' },
			'Customer Mngt': { permissions: [], icon: 'Person' },
			'Promoter Mngt': { permissions: [], icon: 'SupportAgent' },
			'Manage Locations': { permissions: [], icon: 'LocationSearching' },
			'Customer OTP Codes': { permissions: [], icon: 'Security' },
			'Agent OTP Codes': { permissions: [], icon: 'Security' },
			'Business Settings': { permissions: [], icon: 'Business' },
		}},
		'Reports': { permissions: [], icon: 'Paid' },
		'Others': { permissions: [], icon: 'MoreHoriz' }
	};

	// Enhanced permission matching with exact permission name matching first
	permissions.forEach((permission: Permission) => {
		let placed = false;
		const permName = permission.permName;
		const permNameLower = permName.toLowerCase();
		
		// Exact permission name matches first (case sensitive for accuracy)
		switch (permName) {
			case 'Dashboard':
				organized['Dashboard'].permissions.push(permission);
				placed = true;
				break;
			case 'Manage Transactions':
				organized['Manage Transactions'].permissions.push(permission);
				placed = true;
				break;
			case 'Event Management':
				organized['Event Management'].permissions.push(permission);
				placed = true;
				break;
			case 'Float Requests':
				organized['Float Requests'].permissions.push(permission);
				placed = true;
				break;
			case 'Refund Requests':
				organized['Refund Requests'].permissions.push(permission);
				placed = true;
				break;
			case 'Reports':
				organized['Reports'].permissions.push(permission);
				placed = true;
				break;
			
			// Delivery Services parent and children
			case 'Delivery Services':
				organized['Delivery Services'].permissions.push(permission);
				placed = true;
				break;
			case 'Delivery Transactions':
				organized['Delivery Services'].children!['Delivery Transactions'].permissions.push(permission);
				placed = true;
				break;
			case 'Manage Delivery Agents':
				organized['Delivery Services'].children!['Manage Delivery Agents'].permissions.push(permission);
				placed = true;
				break;
			
			// Compliance Mngt parent and children
			case 'Compliance Mngt':
				organized['Compliance Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Customer Compliance':
				organized['Compliance Mngt'].children!['Customer Compliance'].permissions.push(permission);
				placed = true;
				break;
			case 'Merchant Compliance':
				organized['Compliance Mngt'].children!['Merchant Compliance'].permissions.push(permission);
				placed = true;
				break;
			case 'Business Compliance':
				organized['Compliance Mngt'].children!['Business Compliance'].permissions.push(permission);
				placed = true;
				break;
			case 'Agent Compliance':
				organized['Compliance Mngt'].children!['Agent Compliance'].permissions.push(permission);
				placed = true;
				break;
			case 'Delivery Compliance':
				organized['Compliance Mngt'].children!['Delivery Compliance'].permissions.push(permission);
				placed = true;
				break;
			
			// System Mngt parent and children
			case 'System Mngt':
				organized['System Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Delivery Rates':
				organized['System Mngt'].children!['Delivery Rates'].permissions.push(permission);
				placed = true;
				break;
			case 'Exchange Rates':
				organized['System Mngt'].children!['Exchange Rates'].permissions.push(permission);
				placed = true;
				break;
			case 'Service Fees':
				organized['System Mngt'].children!['Service Fees'].permissions.push(permission);
				placed = true;
				break;
			case 'Ticket Settings':
				organized['System Mngt'].children!['Ticket Settings'].permissions.push(permission);
				placed = true;
				break;
			case 'User Mngt':
				organized['System Mngt'].children!['User Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Roles & Permissions':
				organized['System Mngt'].children!['Roles & Permissions'].permissions.push(permission);
				placed = true;
				break;
			case 'Cashback Rules':
				organized['System Mngt'].children!['Cashback Rules'].permissions.push(permission);
				placed = true;
				break;
			case 'Banks':
				organized['System Mngt'].children!['Banks'].permissions.push(permission);
				placed = true;
				break;
			case 'Chart of Accounts':
				organized['System Mngt'].children!['Chart of Accounts'].permissions.push(permission);
				placed = true;
				break;
			case 'Adverts':
				organized['System Mngt'].children!['Adverts'].permissions.push(permission);
				placed = true;
				break;
			case 'Shift Management':
				organized['System Mngt'].children!['Shift Management']?.permissions?.push(permission);
				placed = true;
				break;
			
			// Network Mngt parent and children
			case 'Network Mngt':
				organized['Network Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Agent Mngt':
				organized['Network Mngt'].children!['Agent Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Merchant Mngt':
				organized['Network Mngt'].children!['Merchant Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Customer Mngt':
				organized['Network Mngt'].children!['Customer Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Promoter Mngt':
				organized['Network Mngt'].children!['Promoter Mngt'].permissions.push(permission);
				placed = true;
				break;
			case 'Manage Locations':
				organized['Network Mngt'].children!['Manage Locations'].permissions.push(permission);
				placed = true;
				break;
			case 'Customer OTP Codes':
				organized['Network Mngt'].children!['Customer OTP Codes'].permissions.push(permission);
				placed = true;
				break;
			case 'Agent OTP Codes':
				organized['Network Mngt'].children!['Agent OTP Codes'].permissions.push(permission);
				placed = true;
				break;
			case 'Business Settings':
				organized['Network Mngt'].children!['Business Settings'].permissions.push(permission);
				placed = true;
				break;
		}
		
		// If not matched exactly, try fuzzy matching for related permissions
		if (!placed) {
			// Dashboard related
			if (permNameLower.includes('dashboard')) {
				organized['Dashboard'].permissions.push(permission);
				placed = true;
			}
			// Transaction related (but not delivery transactions)
			else if (permNameLower.includes('transaction') && !permNameLower.includes('delivery')) {
				organized['Manage Transactions'].permissions.push(permission);
				placed = true;
			}
			// Event related
			else if (permNameLower.includes('event')) {
				organized['Event Management'].permissions.push(permission);
				placed = true;
			}
			// Float related
			else if (permNameLower.includes('float')) {
				organized['Float Requests'].permissions.push(permission);
				placed = true;
			}
			// Refund/Reversal related
			else if (permNameLower.includes('refund') || permNameLower.includes('reversal')) {
				organized['Refund Requests'].permissions.push(permission);
				placed = true;
			}
			// Report related
			else if (permNameLower.includes('report')) {
				organized['Reports'].permissions.push(permission);
				placed = true;
			}
			// Delivery related
			else if (permNameLower.includes('delivery')) {
				if (permNameLower.includes('transaction')) {
					organized['Delivery Services'].children!['Delivery Transactions'].permissions.push(permission);
				} else if (permNameLower.includes('agent')) {
					organized['Delivery Services'].children!['Manage Delivery Agents'].permissions.push(permission);
				} else if (permNameLower.includes('compliance')) {
					organized['Compliance Mngt'].children!['Delivery Compliance'].permissions.push(permission);
				} else if (permNameLower.includes('rate')) {
					organized['System Mngt'].children!['Delivery Rates'].permissions.push(permission);
				} else {
					organized['Delivery Services'].permissions.push(permission);
				}
				placed = true;
			}
			// Compliance related
			else if (permNameLower.includes('compliance') || permNameLower.includes('comply')) {
				if (permNameLower.includes('customer')) {
					organized['Compliance Mngt'].children!['Customer Compliance'].permissions.push(permission);
				} else if (permNameLower.includes('merchant')) {
					organized['Compliance Mngt'].children!['Merchant Compliance'].permissions.push(permission);
				} else if (permNameLower.includes('business')) {
					organized['Compliance Mngt'].children!['Business Compliance'].permissions.push(permission);
				} else if (permNameLower.includes('agent')) {
					organized['Compliance Mngt'].children!['Agent Compliance'].permissions.push(permission);
				} else {
					organized['Compliance Mngt'].permissions.push(permission);
				}
				placed = true;
			}
			// System Management related
			else if (permNameLower.includes('exchange') && permNameLower.includes('rate')) {
				organized['System Mngt'].children!['Exchange Rates'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('service') && permNameLower.includes('fee')) {
				organized['System Mngt'].children!['Service Fees'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('ticket')) {
				organized['System Mngt'].children!['Ticket Settings'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('user') && (permNameLower.includes('management') || permNameLower.includes('mngt'))) {
				organized['System Mngt'].children!['User Mngt'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('role') || permNameLower.includes('permission')) {
				organized['System Mngt'].children!['Roles & Permissions'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('cashback')) {
				organized['System Mngt'].children!['Cashback Rules'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('bank')) {
				organized['System Mngt'].children!['Banks'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('chart') || (permNameLower.includes('account') && !permNameLower.includes('balance'))) {
				organized['System Mngt'].children!['Chart of Accounts'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('advert')) {
				organized['System Mngt'].children!['Adverts'].permissions.push(permission);
				placed = true;
			}
			// Network Management related
			else if (permNameLower.includes('agent') && (permNameLower.includes('management') || permNameLower.includes('mngt'))) {
				organized['Network Mngt'].children!['Agent Mngt'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('merchant') && (permNameLower.includes('management') || permNameLower.includes('mngt'))) {
				organized['Network Mngt'].children!['Merchant Mngt'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('customer') && (permNameLower.includes('management') || permNameLower.includes('mngt'))) {
				organized['Network Mngt'].children!['Customer Mngt'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('promoter')) {
				organized['Network Mngt'].children!['Promoter Mngt'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('location')) {
				organized['Network Mngt'].children!['Manage Locations'].permissions.push(permission);
				placed = true;
			}
			else if (permNameLower.includes('otp')) {
				if (permNameLower.includes('customer')) {
					organized['Network Mngt'].children!['Customer OTP Codes'].permissions.push(permission);
				} else if (permNameLower.includes('agent')) {
					organized['Network Mngt'].children!['Agent OTP Codes'].permissions.push(permission);
				} else {
					organized['Network Mngt'].permissions.push(permission);
				}
				placed = true;
			}
			else if (permNameLower.includes('business') && permNameLower.includes('setting')) {
				organized['Network Mngt'].children!['Business Settings'].permissions.push(permission);
				placed = true;
			}
		}
		
		// Place any unmatched permissions in Others
		if (!placed) {
			organized['Others'].permissions.push(permission);
		}
	});

	return organized;
};

export const getParticipantInfo = (transaction: any) => {
	const { type, sender, receiver, agentTransaction, customer } = transaction;

	const result = {
		senderName: '',
		senderPhone: '',
		receiverName: '',
		receiverPhone: '',
		customerName: '',
		customerPhone: '',
		agentName: '',
		agentPhone: '',
	};

	switch (type) {
		case 'topup':
			if (receiver) {
				result.customerName = receiver.fullname || '';
				result.customerPhone = receiver.mobileNumber || '';
			}
			if (agentTransaction?.agent) {
				result.agentName = `${agentTransaction.agent.firstname || ''} ${agentTransaction.agent.lastname || ''}`.trim();
				result.agentPhone = agentTransaction.agent.mobileNumber || '';
			}
			break;

		case 'withdrawal':
			if (sender) {
				result.customerName = sender.fullname || '';
				result.customerPhone = sender.mobileNumber || '';
			}
			if (agentTransaction?.agent) {
				result.agentName = `${agentTransaction.agent.firstname || ''} ${agentTransaction.agent.lastname || ''}`.trim();
				result.agentPhone = agentTransaction.agent.mobileNumber || '';
			}
			break;

		case 'remit':
			if (sender) {
				result.senderName = sender.fullname || '';
				result.senderPhone = sender.mobileNumber || '';
			}
			if (receiver) {
				result.receiverName = receiver.fullname || '';
				result.receiverPhone = receiver.mobileNumber || '';
			}
			break;

		case 'paybill':
			if (sender) {
				result.customerName = sender.fullname || '';
				result.customerPhone = sender.mobileNumber || '';
			}
			break;

		case 'power':
		case 'airtime':
			const customerData = customer || sender;
			if (customerData) {
				result.customerName = customerData.fullname || '';
				result.customerPhone = customerData.mobileNumber || '';
			}
			if (type === 'airtime' && transaction.airtime?.phoneNumber) {
				result.receiverPhone = transaction.airtime.phoneNumber;
			}
			if (type === 'power' && transaction.power?.meterNumber) {
				result.receiverName = `Meter: ${transaction.power.meterNumber}`;
			}
			break;

		case 'banking':
			if (sender) {
				result.customerName = sender.fullname || '';
				result.customerPhone = sender.mobileNumber || '';
			}
			break;

		case 'event':
			const participantData = customer || sender;
			if (participantData) {
				result.customerName = participantData.fullname || '';
				result.customerPhone = participantData.mobileNumber || '';
			}
			break;

		case 'wallet_transaction':
		case 'agent_transaction':
			if (agentTransaction?.agent) {
				result.agentName = `${agentTransaction.agent.firstname || ''} ${agentTransaction.agent.lastname || ''}`.trim();
				result.agentPhone = agentTransaction.agent.mobileNumber || '';
			}
			if (customer) {
				result.customerName = customer.fullname || '';
				result.customerPhone = customer.mobileNumber || '';
			}
			break;

		case 'product':
			const buyerData = customer || sender;
			if (buyerData) {
				result.customerName = buyerData.fullname || '';
				result.customerPhone = buyerData.mobileNumber || '';
			}
			break;

		default:
			if (sender) {
				result.senderName = sender.fullname || '';
				result.senderPhone = sender.mobileNumber || '';
			}
			if (receiver) {
				result.receiverName = receiver.fullname || '';
				result.receiverPhone = receiver.mobileNumber || '';
			}
			if (customer) {
				result.customerName = customer.fullname || '';
				result.customerPhone = customer.mobileNumber || '';
			}
			if (agentTransaction?.agent) {
				result.agentName = `${agentTransaction.agent.firstname || ''} ${agentTransaction.agent.lastname || ''}`.trim();
				result.agentPhone = agentTransaction.agent.mobileNumber || '';
			}
			break;
	}

	return result;
};

export const getParticipantsData = (transaction: any) => {
	const { type, sender, receiver, agentTransaction, customer } = transaction;

	switch (type) {
		case 'topup':
			return {
				participants: [
					...(receiver ? [{ label: 'Customer', name: receiver.fullname, color: 'text-green-600' }] : []),
					...(agentTransaction?.agent ? [{ 
						label: 'Agent', 
						name: `${agentTransaction.agent.firstname} ${agentTransaction.agent.lastname}`, 
						color: 'text-blue-600' 
					}] : [])
				]
			};

		case 'withdrawal':
			return {
				participants: [
					...(sender ? [{ label: 'Customer', name: sender.fullname, color: 'text-orange-600' }] : []),
					...(agentTransaction?.agent ? [{ 
						label: 'Agent', 
						name: `${agentTransaction.agent.firstname} ${agentTransaction.agent.lastname}`, 
						color: 'text-blue-600' 
					}] : [])
				]
			};

		case 'remit':
			return {
				participants: [
					...(sender ? [{ label: 'Sender', name: sender.fullname, color: 'text-purple-600' }] : []),
					...(receiver ? [{ label: 'Receiver', name: receiver.fullname, color: 'text-green-600' }] : [])
				]
			};

		case 'paybill':
			return {
				participants: [
					{ label: 'Customer', name: sender?.fullname || 'N/A', color: 'text-indigo-600' }
				]
			};

		case 'power':
		case 'airtime':
			return {
				participants: [
					{ label: 'Customer', name: customer?.fullname || sender?.fullname || 'N/A', color: 'text-cyan-600' }
				]
			};

		case 'banking':
			return {
				participants: [
					{ label: 'Account Holder', name: sender?.fullname || 'N/A', color: 'text-teal-600' }
				]
			};

		case 'event':
			return {
				participants: [
					{ label: 'Participant', name: customer?.fullname || sender?.fullname || 'N/A', color: 'text-pink-600' }
				]
			};

		case 'wallet_transaction':
		case 'agent_transaction':
			return {
				participants: [
					...(agentTransaction?.agent ? [{ 
						label: 'Agent', 
						name: `${agentTransaction.agent.firstname} ${agentTransaction.agent.lastname}`, 
						color: 'text-blue-600' 
					}] : []),
					...(customer ? [{ label: 'Customer', name: customer.fullname, color: 'text-gray-600' }] : [])
				]
			};

		case 'product':
			return {
				participants: [
					{ label: 'Buyer', name: customer?.fullname || sender?.fullname || 'N/A', color: 'text-emerald-600' }
				]
			};

		case 'kalpeh':
			return {
				participants: [
					{ label: 'Customer', name: sender?.fullname || 'N/A', color: 'text-amber-600' }
				]
			};

		case 'ticketTransfer':
			return {
				participants: [
					...(sender ? [{ label: 'From', name: sender.fullname, color: 'text-violet-600' }] : []),
					...(receiver ? [{ label: 'To', name: receiver.fullname, color: 'text-green-600' }] : [])
				]
			};

		case 'cashback':
		case 'refund':
			return {
				participants: [
					{ label: 'Recipient', name: receiver?.fullname || customer?.fullname || 'N/A', color: 'text-lime-600' }
				]
			};

		default:
			return {
				participants: [
					{ label: '', name: sender?.fullname || receiver?.fullname || customer?.fullname || 'N/A', color: 'text-gray-500' }
				]
			};
	}
};
